/**
 * DEV-only ghost-mode GET mocks for Stories, registered in main.tsx (see shared/lib/dev-ghost-mocks.ts).
 * Lets the pages be design-reviewed at /dev/enter before the API is deployed.
 *
 * Everything is built inside buildFixtures() on first use: the module has no load-time calls, so
 * production builds (where the only caller is dead code behind import.meta.env.DEV) drop it whole.
 * Do not add top-level statements with calls here (security review PR-4/5).
 */
import type { Cast, Episode, Scene, Story, StoryExport, StoryListItem } from './stories.client';

function buildFixtures() {
  const STORY_ID = '11111111-1111-4111-8111-111111111111';
  const iso = (minutesAgo: number) => new Date(Date.now() - minutesAgo * 60_000).toISOString();

  const SYNOPSIS =
    '@Lena, 29, leaves Warsaw on the last night train after deciding to end a seven-year relationship. In her compartment she meets @Marek, a quiet translator going home to Gdańsk. Over a terrible thermos of tea they talk until the train enters a tunnel and the lights go out for eleven seconds. In the dark, Lena admits she is leaving him for good. At dawn in Gdańsk they part without exchanging numbers. Back in her bag, Lena finds Marek’s book with a train ticket for next Friday tucked inside.';

  const CAST: Cast[] = [
    { id: 'cast-lena', alias: 'Lena', character_id: 'char-lena', is_narrator: false, voice_hint: 'soft, slightly husky', photos: [] },
    { id: 'cast-marek', alias: 'Marek', character_id: 'char-marek', is_narrator: false, voice_hint: 'low, warm', photos: [] },
    { id: 'cast-narrator', alias: 'Narrator', character_id: null, is_narrator: true, voice_hint: 'calm', photos: [] },
  ];

  const EP1_SCRIPT = `Scene 1 · Night platform, rain
  @Lena runs for the last train to Gdańsk, red coat soaked. [Lena: panicked → relieved]
  NARRATOR (calm): She almost missed the last train. That was the first lucky thing.

  Scene 2 · Corridor
  Lena drags her suitcase past full compartments, phone buzzing with a name she ignores. [Lena: tense]
  LENA (whispering): Not now. Not ever.

  Scene 3 · The compartment
  @Marek looks up from his book. [Lena: nervous · Marek: intrigued]
  LENA (nervous, out of breath): Is this seat taken?
  MAREK (dry, amused): Only by my bad decisions. Sit.`;

  const EP2_SCRIPT = `Scene 1 · Shared thermos
  Close-up on two hands around a steel thermos cup, rain on the window. [Lena: softening · Marek: warm]
  MAREK (warm): Tea? It is terrible, but it is hot.
  LENA (smiling): Then it is perfect.

  Scene 2 · Tunnel blackout
  The train enters a tunnel, lights flicker off, sparks outside the window.
  NARRATOR (hushed): For eleven seconds, there was only the sound of the rails.`;

  const episodes: Episode[] = [
    { id: 'ep-1', story_id: STORY_ID, position: 0, title: 'The last train', logline: 'Lena runs from her old life and lands in a compartment with a stranger.', beats: ['Sprint through the rain', 'The only free seat', 'An awkward first exchange'], ends_on: '"Is this seat taken?"', script: EP1_SCRIPT, script_rev: 3, exported_at: null, updated_at: iso(120), scenes_total: 3, scenes_ready: 3 },
    { id: 'ep-2', story_id: STORY_ID, position: 1, title: 'Eleven seconds', logline: 'Tea, small talk, then a tunnel turns the lights out and Lena tells the truth.', beats: ['Terrible tea', 'Where are you going', 'Tunnel blackout'], ends_on: 'The lights come back on and neither speaks.', script: EP2_SCRIPT, script_rev: 1, exported_at: null, updated_at: iso(60), scenes_total: 2, scenes_ready: 0 },
    { id: 'ep-3', story_id: STORY_ID, position: 2, title: 'Gdańsk at dawn', logline: 'They arrive, say goodbye without numbers, and Lena finds what he left behind.', beats: ['Sunrise arrival', 'Almost something', 'The ticket'], ends_on: 'A ticket for next Friday, same train.', script: '', script_rev: 0, exported_at: null, updated_at: iso(30), scenes_total: 0, scenes_ready: 0 },
  ];

  const story: Story = {
    id: STORY_ID,
    name: 'Last train to Gdańsk',
    synopsis: SYNOPSIS,
    synopsis_excerpt: SYNOPSIS.replace(/@/g, '').slice(0, 279) + '…',
    tone: 'romance',
    format: '9:16',
    episode_length_s: 90,
    episode_count: 4,
    episodes_total: episodes.length,
    video_model: 'v2.0',
    subtitle_style: 'outline',
    created_at: iso(3000),
    updated_at: iso(30),
    cast: CAST,
    episodes: episodes.map((e) => {
      const summary: Partial<Episode> = { ...e };
      delete summary.script;
      delete summary.story_id;
      return summary as Story['episodes'][number];
    }),
  };

  const LIST: StoryListItem[] = [
    { ...story },
    { id: '22222222-2222-4222-8222-222222222222', name: 'Floor 14', synopsis_excerpt: 'Rivals Nina and Theo get stuck in the office elevator the night before a big pitch.', tone: 'comedy', format: '9:16', episode_length_s: 60, episode_count: 3, episodes_total: 3, video_model: 'v2.0', subtitle_style: 'outline', created_at: iso(5000), updated_at: iso(1440) },
    { id: '33333333-3333-4333-8333-333333333333', name: 'Ninety Seconds', synopsis_excerpt: 'Rafa plans a ninety-second museum heist with Ivy. The vault code is wrong.', tone: 'thriller', format: '16:9', episode_length_s: 90, episode_count: 4, episodes_total: 0, video_model: 'v2.5', subtitle_style: 'strips', created_at: iso(9000), updated_at: iso(6000) },
  ];

  const art = (n: number) => `ghost://take-${n}`;
  const scene = (episodeId: string, i: number, over: Partial<Scene>): Scene => ({
    id: `${episodeId}-s${i + 1}`,
    episode_id: episodeId,
    position: i,
    title: `Scene ${i + 1}`,
    action: '',
    shot: null,
    duration_s: 10,
    moods: [],
    lines: [],
    locked: false,
    content_hash: `h${i}`,
    status: 'draft',
    selected_take_id: null,
    takes: [],
    updated_at: iso(10),
    ...over,
  });
  const take = (id: string, n: number) => ({ id, inference_id: 9000 + n, status: 'completed' as const, video_url: art(n), error_message: null, matches_current: true, created_at: iso(20) });

  const SCENES: Record<string, Scene[]> = {
    'ep-1': [
      scene('ep-1', 0, { title: 'Night platform, rain', action: '@Lena runs for the last train to Gdańsk, red coat soaked.', duration_s: 13, moods: [{ alias: 'Lena', from: 'panicked', to: 'relieved' }], lines: [{ alias: 'Narrator', text: 'She almost missed the last train. That was the first lucky thing.', emotion: 'calm', intensity: 'subtle', delivery: 'soft', direction: 'calm' }], status: 'ready', takes: [take('t1', 1)], selected_take_id: 't1' }),
      scene('ep-1', 1, { title: 'Corridor', action: 'Lena drags her suitcase past full compartments, phone buzzing with a name she ignores.', duration_s: 9, moods: [{ alias: 'Lena', from: 'tense', to: null }], lines: [{ alias: 'Lena', text: 'Not now. Not ever.', emotion: 'neutral', intensity: 'medium', delivery: 'whisper', direction: 'whispering' }], status: 'ready', takes: [take('t2', 2)], selected_take_id: 't2' }),
      scene('ep-1', 2, { title: 'The compartment', action: '@Marek looks up from his book.', shot: 'two_shot', duration_s: 13, moods: [{ alias: 'Lena', from: 'nervous', to: null }, { alias: 'Marek', from: 'intrigued', to: null }], lines: [{ alias: 'Lena', text: 'Is this seat taken?', emotion: 'nervous', intensity: 'medium', delivery: 'normal', direction: 'nervous, out of breath' }, { alias: 'Marek', text: 'Only by my bad decisions. Sit.', emotion: 'dry', intensity: 'subtle', delivery: 'normal', direction: 'dry, amused' }], status: 'ready', takes: [take('t3', 3), { ...take('t3b', 4), matches_current: false }], selected_take_id: 't3' }),
    ],
    'ep-2': [
      scene('ep-2', 0, { title: 'Shared thermos', action: 'Close-up on two hands around a steel thermos cup, rain on the window.', shot: 'close_up', duration_s: 14, moods: [{ alias: 'Lena', from: 'softening', to: null }, { alias: 'Marek', from: 'warm', to: null }], lines: [{ alias: 'Marek', text: 'Tea? It is terrible, but it is hot.', emotion: 'warm', intensity: 'medium', delivery: 'normal', direction: 'warm' }, { alias: 'Lena', text: 'Then it is perfect.', emotion: 'smiling', intensity: 'medium', delivery: 'normal', direction: 'smiling' }], status: 'draft', takes: [] }),
      scene('ep-2', 1, { title: 'Tunnel blackout', action: 'The train enters a tunnel, lights flicker off, sparks outside the window.', duration_s: 13, lines: [{ alias: 'Narrator', text: 'For eleven seconds, there was only the sound of the rails.', emotion: 'neutral', intensity: 'subtle', delivery: 'whisper', direction: 'hushed' }], status: 'rendering', takes: [{ id: 't6', inference_id: 9006, status: 'processing', video_url: null, error_message: null, matches_current: true, created_at: iso(1) }] }),
    ],
    'ep-3': [],
  };

  const EXPORTS: Record<string, StoryExport[]> = {
    'ep-1': [
      { id: 'x-ready', episode_id: 'ep-1', status: 'ready', resolution: '1080p', subtitles: 'srt', subtitle_style: null, duration_s: 35, download_url: '#ghost-download', srt_url: '#ghost-srt', error_code: null, created_at: iso(45), completed_at: iso(44) },
    ],
  };

  // ─── Admin (contract PR-6 "Admin") ───────────────────────────────────────
  const stats = (renders: number, credits: number, unsaved = 0, live = 0) => ({ episodes: 3, scenes: 5, renders, renders_live: live, credits_spent: credits, unarchived_takes: unsaved });
  const ADMIN_LIST = {
    items: [
      { id: STORY_ID, team_id: 1, team_name: 'Ghost Studio', name: story.name, tone: 'romance', format: '9:16', deleted_at: null, created_at: iso(3000), updated_at: iso(30), ...stats(6, 412.5, 1, 1) },
      { id: '22222222-2222-4222-8222-222222222222', team_id: 82, team_name: 'Northwind Media', name: 'Floor 14', tone: 'comedy', format: '9:16', deleted_at: null, created_at: iso(5000), updated_at: iso(1440), ...stats(14, 980, 0) },
      { id: '33333333-3333-4333-8333-333333333333', team_id: 82, team_name: 'Northwind Media', name: 'Ninety Seconds', tone: 'thriller', format: '16:9', deleted_at: iso(600), created_at: iso(9000), updated_at: iso(600), ...stats(0, 0) },
    ],
    total: 3,
  };
  const AI_RUNS = [
    { id: 'ai-1', team_id: 1, story_id: STORY_ID, episode_id: null, user_id: 'dev-ghost', kind: 'split' as const, status: 'ok' as const, model: 'ep-20260319213955-smkhq', tokens_in: 1840, tokens_out: 2960, error_code: null, created_at: iso(2900) },
    { id: 'ai-2', team_id: 1, story_id: STORY_ID, episode_id: 'ep-2', user_id: 'dev-ghost', kind: 'draft' as const, status: 'blocked' as const, model: null, tokens_in: null, tokens_out: null, error_code: 'prompt_blocked', created_at: iso(200) },
    { id: 'ai-3', team_id: 82, story_id: '22222222-2222-4222-8222-222222222222', episode_id: null, user_id: 'u-2', kind: 'draft' as const, status: 'pending' as const, model: null, tokens_in: null, tokens_out: null, error_code: null, created_at: iso(12) },
  ];
  const adminTake = (id: string, n: number, over: Record<string, unknown> = {}) => ({
    id, inference_id: 9000 + n, inference_aurous_id: `vid_01GHOST${n}`, inference_status: 'completed', error_message: null,
    matches_scene: true, archived: true, archived_at: iso(19), archive_attempts: 1, created_at: iso(20), ...over,
  });
  const ADMIN_DETAIL = {
    story: { id: STORY_ID, team_id: 1, name: story.name, synopsis: SYNOPSIS, tone: 'romance', format: '9:16', episode_length_s: 90, episode_count: 4, deleted_at: null, created_at: iso(3000), updated_at: iso(30) },
    stats: stats(6, 412.5, 1, 1),
    team_paused: { paused: false, reason: null, paused_at: null, paused_by: null, paused_by_email: null },
    cast: CAST.map((c) => ({ id: c.id, alias: c.alias, character_id: c.character_id, is_narrator: c.is_narrator, voice_hint: c.voice_hint })),
    episodes: episodes.map((e) => ({
      id: e.id, position: e.position, title: e.title, logline: e.logline, script: e.script, script_rev: e.script_rev, exported_at: e.exported_at, updated_at: e.updated_at,
      scenes: (SCENES[e.id] ?? []).map((s, i) => ({
        id: s.id, position: s.position, title: s.title, action: s.action, shot: s.shot, duration_s: s.duration_s, lines: s.lines, locked: s.locked,
        selected_take_id: s.selected_take_id, render_claim_until: e.id === 'ep-2' && i === 1 ? iso(4) : null,
        takes: s.takes.map((t, j) =>
          adminTake(t.id, 10 * i + j, t.status === 'completed'
            ? (e.id === 'ep-1' && i === 2 && j === 0 ? { archived: false, archived_at: null, archive_attempts: 5 } : {})
            : { inference_status: t.status === 'failed' ? 'failed' : 'processing', archived: false, archived_at: null, archive_attempts: 0, error_message: t.error_message }),
        ),
      })),
    })),
    ai_runs: AI_RUNS.filter((r) => r.story_id === STORY_ID),
    exports: (EXPORTS['ep-1'] ?? []).map((x) => ({ ...x, attempts: 1 })),
  };
  const HEALTH = {
    unarchived_over_1h: 1, archive_attempts_exhausted: 1, archive_window_missed: 0, expired_render_claims: 0,
    exports_queued: 0, exports_processing: 1, exports_failed_24h: 1, ai_runs_stuck_pending: 0, last_archived_at: iso(19),
  };

  return { STORY_ID, LIST, SCENES, episodes, story, EXPORTS, ADMIN_LIST, ADMIN_DETAIL, AI_RUNS, HEALTH };
}

/** The writing assistant's estimate basis in ghost mode (GET /ai/pricing and /stories/ai/usage). */
const AI_RATES = Object.fromEntries(
  ([['split', 8000], ['draft', 4000], ['assist', 3000], ['series', 3000]] as const).map(([kind, max]) => [
    kind,
    { key: `stories.${kind}`, available: true, credits_per_1k_input: 0.08, credits_per_1k_output: 0.45, min_credits: 0.1, max_output_tokens: max },
  ]),
);

let fixtures: ReturnType<typeof buildFixtures> | undefined;

/** Returns mock data for a Stories GET path, or undefined. */
export function storiesGhostMock(path: string): unknown | undefined {
  const { STORY_ID, LIST, SCENES, episodes, story, EXPORTS, ADMIN_LIST, ADMIN_DETAIL, AI_RUNS, HEALTH } = (fixtures ??= buildFixtures());
  if (path.endsWith('/stories/ai/usage')) {
    const kinds = Object.fromEntries(Object.values(AI_RATES).map((r) => [r.key.split('.')[1], r]));
    return { used: 3, credits_used: 1.82, prices: kinds, stories_paused: false, adult_content: false };
  }
  if (path.includes('/ai/pricing')) return { feature: 'stories', kinds: Object.values(AI_RATES) };
  // Before the /stories/:id match, which would swallow it.
  if (path.endsWith('/stories/usage')) {
    return {
      since: new Date(Date.now() - 30 * 86_400_000).toISOString(),
      until: new Date().toISOString(),
      // Kept below the usage mock's credit total: the card says it is included in it.
      credits: 412.6,
      renders: 5,
      failed_renders: 1,
      by_story: LIST.slice(0, 3).map((s, i) => ({ story_id: s.id, name: s.name, credits: [221.4, 123.8, 67.4][i], renders: [2, 2, 1][i] })),
      truncated: false,
    };
  }
  // Admin first: the generic /stories/:id match below would swallow these.
  if (path.endsWith('/admin/stories')) return ADMIN_LIST;
  if (path.endsWith('/admin/stories/health')) return HEALTH;
  if (path.endsWith('/admin/stories/ai-runs')) {
    return { items: AI_RUNS, total: AI_RUNS.length, page_totals: { tokens_in: 1840, tokens_out: 2960 } };
  }
  if (/\/admin\/stories\/[^/]+$/.test(path)) return ADMIN_DETAIL;
  if (/\/admin\/teams\/\d+\/stories-pause$/.test(path)) {
    return { paused: false, reason: null, paused_at: null, paused_by: null, paused_by_email: null };
  }
  const exportsMatch = path.match(/\/stories\/episodes\/([^/]+)\/exports$/);
  if (exportsMatch) return EXPORTS[exportsMatch[1]] ?? [];
  const exportMatch = path.match(/\/stories\/exports\/([^/]+)$/);
  if (exportMatch) return Object.values(EXPORTS).flat().find((x) => x.id === exportMatch[1]);
  if (path.endsWith('/stories')) return LIST;
  const scenesMatch = path.match(/\/stories\/episodes\/([^/]+)\/scenes$/);
  if (scenesMatch) return SCENES[scenesMatch[1]] ?? [];
  const episodeMatch = path.match(/\/stories\/episodes\/([^/]+)$/);
  if (episodeMatch) return episodes.find((e) => e.id === episodeMatch[1]) ?? episodes[0];
  const storyMatch = path.match(/\/stories\/([^/]+)$/);
  if (storyMatch) {
    const listItem = LIST.find((s) => s.id === storyMatch[1]);
    if (!listItem || listItem.id === STORY_ID) return story;
    // Same episode count as the list row (founder review: "3 episodes" on the list, "0" inside).
    const summaries = Array.from({ length: listItem.episodes_total }, (_, i) => ({
      id: `${listItem.id}-ep-${i + 1}`,
      position: i,
      title: `Episode ${i + 1}`,
      logline: '',
      beats: [],
      ends_on: '',
      script_rev: 0,
      exported_at: null,
      updated_at: listItem.updated_at,
      scenes_total: 0,
      scenes_ready: 0,
    }));
    return { ...listItem, synopsis: listItem.synopsis_excerpt, cast: [], episodes: summaries } satisfies Story;
  }
  return undefined;
}
